import { axiosRubyClient } from '@/util/axios/axiosClient';

// The badge/certificate catalog — what a client can be required to have and
// what a worker can be granted. Distinct from `../Certificates/api.ts`, which
// is worker-uploaded certificate PHOTO verification.
export type CertificateTypeLabel = 'BADGE' | 'CERTIFICATE';

export interface CertificateUsage {
  workers: number;
  customers: number;
  upcoming_jobs: number;
}

export interface Certificate {
  id: number;
  name: string;
  display_name_en: string | null;
  display_name_es: string | null;
  description_en: string | null;
  description_es: string | null;
  certificate_type: CertificateTypeLabel;
  image_key: string | null;
  image_url: string | null;
  gravy_trained: boolean | null;
  skill_id: number | null;
  deleted: boolean;
  agency_id: number;
  tenant_id: number;
  usage: CertificateUsage;
  created_at: string | null;
  updated_at: string | null;
}

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ListCertificatesResponse {
  success: boolean;
  data: Certificate[];
  pagination?: Pagination;
}

export interface SingleCertificateResponse {
  success: boolean;
  data: Certificate;
}

export interface ListParams {
  certificate_type?: CertificateTypeLabel | '';
  search?: string;
  include_deleted?: boolean;
  page?: number;
  per_page?: number;
}

export interface CertificateInput {
  name: string;
  display_name_en?: string;
  display_name_es?: string;
  description_en?: string;
  description_es?: string;
  certificate_type?: number;
  image_key?: string | null;
}

export const listCertificates = async (
  params: ListParams = {},
): Promise<ListCertificatesResponse> => {
  const response = await axiosRubyClient.get('/api/admin/certificates', {
    params,
  });
  return response.data;
};

export const createCertificate = async (
  input: CertificateInput,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.post('/api/admin/certificates', input);
  return response.data;
};

export const updateCertificate = async (
  id: number,
  input: Partial<CertificateInput>,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.patch(
    `/api/admin/certificates/${id}`,
    input,
  );
  return response.data;
};

// GRAVY-10787: restore a soft-deleted row. The API accepts `deleted: false` on
// update as the ONLY edit a deleted certificate takes; retiring one still has to
// go through Certificates::DeleteService, which is now run manually by an
// engineer — there is no delete endpoint (GRAVY-10807).
export const restoreCertificate = async (
  id: number,
): Promise<SingleCertificateResponse> => {
  const response = await axiosRubyClient.patch(
    `/api/admin/certificates/${id}`,
    {
      deleted: false,
    },
  );
  return response.data;
};

// Axios rejects on 4xx, so the server's `errors` array lives on the error.
// GRAVY-10798: the 422 for a duplicate name now carries the conflicting record,
// so the UI can tell an admin the clash is with a DELETED row and offer Restore
// instead of leaving them at a dead end.
export interface DuplicateConflict {
  id: number;
  name: string;
  deleted: boolean;
  certificate_type: CertificateTypeLabel;
}

export const conflictFrom = (err: any): DuplicateConflict | null =>
  err?.response?.data?.conflict ?? null;

export const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return err?.message || fallback;
};

export const isConflict = (err: any): boolean => err?.response?.status === 409;
